import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { DataGrid } from '@mui/x-data-grid';
import { Box, Button, TextField, Paper, Grid, Typography, Modal, Chip } from '@mui/material';
import { styled } from '@mui/system';
import API_URL from '../apiConfig';
import { OrderDetails } from './OrderDetails';

const BakeryButton = styled(Button)({
    background: 'linear-gradient(45deg, #e88ba9 0%, #f4a2bc 100%)',
    borderRadius: '20px',
    border: 0,
    color: 'white',
    padding: '8px 20px',
    boxShadow: '0 4px 10px rgba(232, 139, 169, 0.3)',
    fontWeight: 600,
    textTransform: 'none',
    '&:hover': {
        background: 'linear-gradient(45deg, #f4a2bc 0%, #e88ba9 100%)',
        boxShadow: '0 6px 15px rgba(232, 139, 169, 0.4)',
    },
});

const OutlineButton = styled(Button)({
    borderRadius: '20px',
    color: '#d46a89',
    borderColor: '#e88ba9',
    padding: '7px 19px',
    fontWeight: 600,
    textTransform: 'none',
    '&:hover': {
        borderColor: '#d46a89',
        backgroundColor: '#fcf4f4',
    },
});

const SoftTextField = styled(TextField)({
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        '&.Mui-focused fieldset': {
            borderColor: '#e88ba9',
            borderWidth: '2px',
        },
    },
    '& .MuiInputLabel-root.Mui-focused': {
        color: '#e88ba9',
    }
});

const StatusChip = ({ status }) => {
    let color = 'default';
    if (status?.toLowerCase() === 'pending') color = 'warning';
    if (status?.toLowerCase() === 'processing') color = 'info';
    if (status?.toLowerCase() === 'delivered' || status?.toLowerCase() === 'completed') color = 'success';
    if (status?.toLowerCase() === 'cancelled') color = 'error';

    return <Chip label={status || 'Pending'} color={color} size="small" sx={{ fontWeight: 600 }} />;
};

const AdminOrders = () => {
    const [orders, setOrders] = useState([]);
    const [order_id, setOrder_id] = useState('');
    const [status, setStatus] = useState('');
    const [open, setOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);

    const handleClick = async (e) => {
        e.preventDefault();
        if (!order_id) return alert("Please select an order ID");
        try {
            await axios.post(`${API_URL}/api/orders/admin`, { order_id, status }, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            fetchOrders();
            setOrder_id("");
            setStatus("");
        } catch (error) {
            console.error('Error updating order:', error);
        }
    };

    useEffect(() => {
        fetchOrders();
    }, []);

    const fetchOrders = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/orders/admin`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            response.data.forEach(order => {
                order.item_names = order.items.map(item => item.name).join(', ');
                order.date = new Date(order.orderPlacedTime).toLocaleDateString();
            });
            setOrders(response.data);
        } catch (error) {
            console.error('Error fetching orders:', error);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this order?")) return;
        try {
            await axios.delete(`${API_URL}/api/orders/admin/${id}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            fetchOrders();
        } catch (error) {
            console.error('Error deleting order:', error);
        }
    };

    const handleView = (order) => {
        setSelectedOrder(order);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedOrder(null);
    };

    const columns = [
        { field: '_id', headerName: 'ID', width: 220 },
        { field: 'date', headerName: 'Date', width: 120 },
        { field: 'deliveryAddress', headerName: 'Delivery', width: 250 },
        { field: 'item_names', headerName: 'Order Details', width: 300 },
        { field: 'totalPrice', headerName: 'Total (LKR)', width: 120 },
        {
            field: 'status',
            headerName: 'Status',
            width: 130,
            renderCell: (params) => <StatusChip status={params.row.status} />
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 250,
            renderCell: (params) => (
                <Box display="flex" gap="8px" alignItems="center" height="100%">
                    <OutlineButton variant="outlined" size="small" onClick={() => { setOrder_id(params.row._id); setStatus(params.row.status || 'Pending'); }}>
                        Edit
                    </OutlineButton>
                    <OutlineButton variant="outlined" size="small" onClick={() => handleView(params.row)}>
                        View
                    </OutlineButton>
                    <Button sx={{ minWidth: 0, p: '4px 10px', color: '#e53935' }} size="small" onClick={() => handleDelete(params.row._id)}>
                        ✕
                    </Button>
                </Box>
            ),
        },
    ];

    return (
        <React.Fragment>
            <Typography variant="h4" sx={{ fontFamily: '"Playfair Display", serif', color: '#d46a89', mb: 3 }}>
                Order Management
            </Typography>

            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={5}>
                    <Paper elevation={0} sx={{ p: 4, borderRadius: '24px', border: '1px solid #f4a2bc', background: '#fff' }}>
                        <Typography variant='h6' sx={{ color: '#5c434a', mb: 3, fontWeight: 700 }}>Quick Update Status</Typography>
                        <Box component='form' display="flex" flexDirection="column" gap={2}>
                            <SoftTextField
                                size="small"
                                name='order_id'
                                label="Selected Order ID"
                                value={order_id}
                                disabled
                                sx={{ background: '#fcf4f4' }}
                            />
                            <SoftTextField
                                size="small"
                                name='status'
                                label="New Status (e.g., Prepared, Delivered)"
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                            />
                            <BakeryButton onClick={handleClick} disabled={!order_id} sx={{ mt: 1 }}>
                                Update Status
                            </BakeryButton>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={7}>
                    {/* Placeholder for future daily stats or charts */}
                    <Box sx={{ height: '100%', borderRadius: '24px', background: 'linear-gradient(135deg, #fcf4f4 0%, #fff 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed #f4a2bc' }}>
                        <Typography sx={{ color: '#e88ba9', fontWeight: 500 }}>Select 'Edit' on an order below to update its status.</Typography>
                    </Box>
                </Grid>
            </Grid>

            <Paper elevation={0} sx={{ p: 2, borderRadius: '24px', border: '1px solid rgba(232, 139, 169, 0.2)', boxShadow: '0 10px 30px rgba(0,0,0,0.02)' }}>
                <DataGrid
                    rows={orders}
                    columns={columns}
                    pageSize={10}
                    rowsPerPageOptions={[10, 20]}
                    disableSelectionOnClick
                    getRowId={(row) => row._id}
                    autoHeight
                    sx={{
                        border: 'none',
                        '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: '#fffafb',
                            color: '#8c7077',
                            fontSize: '14px',
                            fontWeight: 700,
                            borderBottom: '2px solid #fcf4f4'
                        },
                        '& .MuiDataGrid-row': {
                            '&:hover': {
                                backgroundColor: '#fffafb',
                            },
                        },
                        '& .MuiDataGrid-cell': {
                            borderBottom: '1px solid #fcf4f4',
                            color: '#5c434a'
                        }
                    }}
                />
            </Paper>

            <Modal open={open} onClose={handleClose} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{ width: '600px', maxWidth: '90vw', maxHeight: '90vh', overflow: 'auto', p: 4, backgroundColor: 'white', borderRadius: '24px', outline: 'none', boxShadow: '0 20px 50px rgba(0,0,0,0.1)' }}>
                    <Typography variant="h5" sx={{ color: '#d46a89', mb: 2, fontFamily: '"Playfair Display", serif' }}>Order Invoice</Typography>
                    <OrderDetails item={selectedOrder} />
                    <Box mt={3} display="flex" justifyContent="flex-end">
                        <BakeryButton onClick={handleClose}>Close</BakeryButton>
                    </Box>
                </Box>
            </Modal>
        </React.Fragment>
    );
};

export default AdminOrders;
